
import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.domain.usecase.DeletePostUseCase
import com.example.readingnotes.domain.usecase.HandleStripeConnectCallbackUseCase
import com.example.readingnotes.domain.usecase.RegisterUserUseCase
import com.example.readingnotes.domain.usecase.SetupTotpUseCase
import com.example.readingnotes.domain.usecase.UpdatePostUseCase
import com.example.readingnotes.domain.usecase.VerifyTotpUseCase
import kotlinx.browser.document
import kotlinx.browser.window
import com.example.readingnotes.presentation.admin.AdminPostEditorEvent
import com.example.readingnotes.presentation.admin.AdminPostEditorViewModel
import com.example.readingnotes.presentation.auth.AuthEvent
import com.example.readingnotes.presentation.auth.AuthViewModel
import com.example.readingnotes.presentation.postdetail.PostDetailEvent
import com.example.readingnotes.presentation.postdetail.PostDetailViewModel
import com.example.readingnotes.presentation.postlist.PostListEvent
import com.example.readingnotes.presentation.postlist.PostListViewModel
import com.example.readingnotes.presentation.postlist.SortOption
import com.example.readingnotes.presentation.theme.ThemeState
import com.example.readingnotes.presentation.theme.ThemeViewModel
import com.example.readingnotes.util.DateFormatter
import design.Theme
import screens.auth.AdminLoginScreen
import screens.auth.EmailLoginScreen
import screens.auth.OidcCallbackScreen
import screens.auth.OidcLoginScreen
import screens.auth.ProfileCompletionScreen
import screens.auth.RegisterScreen
import screens.auth.TotpVerifyScreen
import screens.auth.TotpSetupScreen
import screens.post.PostListScreen
import screens.post.PostDetailScreen
import screens.admin.AdminPostEditorScreen
import screens.notification.NotificationHistoryScreen
import screens.notification.NotificationSettingsScreen
import screens.admin.StripeConnectScreen
import screens.admin.SalesDashboardScreen
import components.StatusSelector
import utils.handleImageUpload
import styles.AppStyleSheet
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.renderComposable

// API base URL - can be configured via environment or defaults to localhost
val apiBaseUrl: String = js("window.API_BASE_URL || 'http://localhost:3001'") as String
val appModule = AppModule(baseUrl = apiBaseUrl)
val appScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

// Global AuthViewModel for app-wide authentication state management
val globalAuthViewModel =
    AuthViewModel(
        adminLoginUseCase = appModule.adminLoginUseCase,
        authRepository = appModule.authRepository,
        viewModelScope = appScope,
        registerUserUseCase = RegisterUserUseCase(appModule.authRepository),
        verifyTotpUseCase = VerifyTotpUseCase(appModule.authRepository),
        setupTotpUseCase = SetupTotpUseCase(appModule.authRepository),
    )

// Global ThemeViewModel
val globalThemeViewModel = ThemeViewModel()

fun main() {
    renderComposable(rootElementId = "root") {
        App()
    }
}

@Composable
fun App() {
    var currentRoute by remember { mutableStateOf("postList") }
    var selectedPostId by remember { mutableStateOf<String?>(null) }
    var editingPostId by remember { mutableStateOf<String?>(null) }
    val authState by globalAuthViewModel.state.collectAsState()
    val themeState by globalThemeViewModel.state.collectAsState()

    // Route parsing function
    fun updateRouteFromPath(path: String) {
        when {
            path == "/" || path.isEmpty() -> {
                currentRoute = "postList"
            }
            path == "/notifications" -> {
                currentRoute = "notificationHistory"
            }
            path == "/notification-settings" -> {
                currentRoute = "notificationSettings"
            }
            path == "/auth/oidc/login" -> {
                currentRoute = "oidcLogin"
            }
            path.startsWith("/auth/oidc/callback") -> {
                currentRoute = "oidcCallback"
            }
            path == "/auth/profile-complete" -> {
                currentRoute = "profileCompletion"
            }
            path == "/settings/stripe-connect" -> {
                if (authState.isLoggedIn) {
                    currentRoute = "stripeConnect"
                } else {
                    currentRoute = "emailLogin"
                }
            }
            path == "/settings/sales" -> {
                if (authState.isLoggedIn) {
                    currentRoute = "salesDashboard"
                } else {
                    currentRoute = "emailLogin"
                }
            }
            path.startsWith("/post/") -> {
                val postId = path.substringAfterLast("/").takeIf { it.isNotBlank() }
                if (postId != null) {
                    selectedPostId = postId
                    currentRoute = "postDetail"
                } else {
                    currentRoute = "notFound"
                }
            }
            path == "/admin" -> {
                currentRoute = "emailLogin"
            }
            path == "/admin/new" -> {
                if (authState.isLoggedIn) {
                    editingPostId = null
                    currentRoute = "adminPostEditor"
                } else {
                    currentRoute = "emailLogin"
                }
            }
            path.startsWith("/admin/edit/") -> {
                val postId = path.substringAfterLast("/").takeIf { it.isNotBlank() }
                if (postId != null && authState.isLoggedIn) {
                    editingPostId = postId
                    currentRoute = "adminPostEditor"
                } else {
                    currentRoute = "emailLogin"
                }
            }
            else -> {
                currentRoute = "notFound"
            }
        }
    }

    // Parse URL on app startup and handle browser navigation
    LaunchedEffect(Unit) {
        // Initialize design system theme
        design.Theme.init()

        // Check authentication state on startup
        globalAuthViewModel.onEvent(AuthEvent.OnCheckAuthState)

        // Parse current URL after a short delay to ensure DOM is ready
        kotlinx.coroutines.delay(100)
        val path = window.location.pathname

        // Handle Stripe Connect OAuth callback (?code= parameter)
        val urlParams = window.location.search
        if (urlParams.contains("?code=") || urlParams.contains("&code=")) {
            // Extract authorization code from URL
            val codeMatch = Regex("[?&]code=([^&]+)").find(urlParams)
            val authCode = codeMatch?.groupValues?.get(1)

            if (authCode != null) {
                // Handle Stripe Connect OAuth callback
                val handleCallbackUseCase = HandleStripeConnectCallbackUseCase(appModule.stripeConnectRepository)
                val result = handleCallbackUseCase(authCode)

                result.onSuccess { callbackResult ->
                    if (callbackResult.success) {
                        console.log("Stripe Connect completed successfully")
                    } else {
                        console.log("Stripe Connect completed but onboarding not finished")
                    }
                }.onFailure { error ->
                    console.error("Stripe Connect callback failed:", error)
                }

                // Redirect to Stripe Connect settings page (remove code parameter)
                window.history.replaceState(null, "", "/settings/stripe-connect")
                currentRoute = "stripeConnect"
                return@LaunchedEffect
            }
        }

        updateRouteFromPath(path)
    }

    // Re-evaluate route when authentication state changes
    // This ensures that protected routes are handled correctly after auth state is loaded
    LaunchedEffect(authState.isLoggedIn) {
        // Only re-evaluate if this is not the initial render
        // (the initial render is handled by the LaunchedEffect(Unit) above)
        val path = window.location.pathname

        // For protected routes, re-evaluate when auth state changes
        if (path.startsWith("/admin") || path.startsWith("/admin/new") || path.startsWith("/admin/edit/")) {
            updateRouteFromPath(path)
        }
    }

    // Handle browser back/forward navigation
    DisposableEffect(Unit) {
        val handlePopState: (org.w3c.dom.events.Event) -> Unit = {
            val path = window.location.pathname
            updateRouteFromPath(path)
        }

        window.addEventListener("popstate", handlePopState)

        onDispose {
            window.removeEventListener("popstate", handlePopState)
        }
    }


    // Apply theme changes to DOM
    LaunchedEffect(themeState.isDarkMode) {
        // Sync KMP ThemeViewModel with Web's design.Theme
        // Convert KMP ThemeMode to Web ThemeMode using valueOf
        val webThemeMode = try {
            Theme.ThemeMode.valueOf(themeState.themeMode.name)
        } catch (e: Exception) {
            // Fallback to SYSTEM if enum value doesn't match
            Theme.ThemeMode.SYSTEM
        }
        Theme.setThemeMode(webThemeMode)
        Theme.applyTheme(webThemeMode)

        // Also set class attribute for E2E tests
        if (themeState.isDarkMode) {
            document.documentElement?.setAttribute("class", "dark")
            document.documentElement?.classList?.add("dark")
            document.body?.classList?.add("dark")
        } else {
            document.documentElement?.setAttribute("class", "")
            document.documentElement?.classList?.remove("dark")
            document.body?.classList?.remove("dark")
        }
    }

    Style(AppStyleSheet)


    Div({ classes(AppStyleSheet.container) }) {
        when {
            currentRoute == "postList" -> {
                PostListScreen(
                    onPostClick = { postId ->
                        selectedPostId = postId
                        currentRoute = "postDetail"
                        window.history.pushState(null, "", "/post/$postId")
                    },
                    onAdminClick = {
                        // If already logged in, go directly to editor (Requirements 4.2)
                        if (authState.isLoggedIn) {
                            editingPostId = null
                            currentRoute = "adminPostEditor"
                            window.history.pushState(null, "", "/admin/new")
                        } else {
                            currentRoute = "emailLogin"
                            window.history.pushState(null, "", "/admin")
                        }
                    },
                    isLoggedIn = authState.isLoggedIn,
                    onLogoutClick = {
                        globalAuthViewModel.onEvent(AuthEvent.OnLogout)
                    },
                    appModule = appModule,
                    appScope = appScope,
                    themeViewModel = globalThemeViewModel,
                    onNotificationHistoryClick = {
                        currentRoute = "notificationHistory"
                        window.history.pushState(null, "", "/notifications")
                    },
                    onNotificationSettingsClick = {
                        currentRoute = "notificationSettings"
                        window.history.pushState(null, "", "/notification-settings")
                    },
                )
            }
            currentRoute == "postDetail" && selectedPostId != null -> {
                // Safe to use !! because we already checked != null above
                val postId = selectedPostId!!
                PostDetailScreen(
                    postId = postId,
                    onBackClick = {
                        currentRoute = "postList"
                        window.history.pushState(null, "", "/")
                    },
                    isLoggedIn = authState.isLoggedIn,
                    onEditClick = { postId ->
                        editingPostId = postId
                        currentRoute = "adminPostEditor"
                        window.history.pushState(null, "", "/admin/edit/$postId")
                    },
                    appModule = appModule,
                    appScope = appScope,
                )
            }
            currentRoute == "adminLogin" -> {
                AdminLoginScreen(
                    onLoginSuccess = {
                        // Check if TOTP verification is required
                        if (authState.totpRequired && !authState.totpVerified) {
                            currentRoute = "totpVerify"
                        } else {
                            editingPostId = null
                            currentRoute = "adminPostEditor"
                            window.history.pushState(null, "", "/admin/new")
                        }
                    },
                    onBackClick = {
                        currentRoute = "postList"
                        window.history.pushState(null, "", "/")
                    },
                    onRegisterClick = { currentRoute = "register" },
                    onEmailLoginClick = { currentRoute = "emailLogin" },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "oidcLogin" -> {
                OidcLoginScreen(
                    onLoginSuccess = {
                        // Check if TOTP verification is required
                        if (authState.totpRequired && !authState.totpVerified) {
                            currentRoute = "totpVerify"
                        } else {
                            editingPostId = null
                            currentRoute = "adminPostEditor"
                            window.history.pushState(null, "", "/admin/new")
                        }
                    },
                    onNavigateToAdmin = {
                        currentRoute = "adminLogin"
                        window.history.pushState(null, "", "/admin")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "oidcCallback" -> {
                OidcCallbackScreen(
                    onLoginSuccess = {
                        // Check if profile completion is required
                        if (authState.userProfile == null || !authState.userProfile!!.profileComplete) {
                            currentRoute = "profileCompletion"
                            window.history.pushState(null, "", "/auth/profile-complete")
                        } else if (authState.totpRequired && !authState.totpVerified) {
                            currentRoute = "totpVerify"
                        } else {
                            editingPostId = null
                            currentRoute = "adminPostEditor"
                            window.history.pushState(null, "", "/admin/new")
                        }
                    },
                    onNavigateToLogin = {
                        currentRoute = "oidcLogin"
                        window.history.pushState(null, "", "/auth/oidc/login")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "profileCompletion" -> {
                ProfileCompletionScreen(
                    onProfileComplete = {
                        // Navigate to admin after profile completion
                        editingPostId = null
                        currentRoute = "adminPostEditor"
                        window.history.pushState(null, "", "/admin/new")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "emailLogin" -> {
                EmailLoginScreen(
                    onLoginSuccess = {
                        // Check if profile completion is required
                        if (authState.userProfile == null || !authState.userProfile!!.profileComplete) {
                            currentRoute = "profileCompletion"
                            window.history.pushState(null, "", "/auth/profile-complete")
                        } else if (authState.totpRequired && !authState.totpVerified) {
                            currentRoute = "totpVerify"
                        } else {
                            editingPostId = null
                            currentRoute = "adminPostEditor"
                            window.history.pushState(null, "", "/admin/new")
                        }
                    },
                    onNavigateToLogin = {
                        currentRoute = "postList"
                        window.history.pushState(null, "", "/")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "register" -> {
                RegisterScreen(
                    onRegisterSuccess = {
                        editingPostId = null
                        currentRoute = "adminPostEditor"
                        window.history.pushState(null, "", "/admin/new")
                    },
                    onNavigateToLogin = {
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "totpVerify" -> {
                TotpVerifyScreen(
                    onVerifySuccess = {
                        editingPostId = null
                        currentRoute = "adminPostEditor"
                        window.history.pushState(null, "", "/admin/new")
                    },
                    onBackClick = {
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "totpSetup" -> {
                TotpSetupScreen(
                    onSetupComplete = {
                        editingPostId = null
                        currentRoute = "adminPostEditor"
                        window.history.pushState(null, "", "/admin/new")
                    },
                    onBackClick = {
                        // Navigate back to email login screen when user cancels TOTP setup
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    },
                    authViewModel = globalAuthViewModel,
                )
            }
            currentRoute == "adminPostEditor" -> {
                // Redirect to login if not authenticated (Requirements 4.3)
                if (!authState.isLoggedIn) {
                    LaunchedEffect(Unit) {
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    }
                } else {
                    AdminPostEditorScreen(
                        postId = editingPostId,
                        onPostSuccess = {
                            editingPostId = null
                            currentRoute = "postList"
                            window.history.pushState(null, "", "/")
                        },
                        onBackClick = {
                            editingPostId = null
                            currentRoute = "postList"
                            window.history.pushState(null, "", "/")
                        },
                        onLogoutClick = {
                            globalAuthViewModel.onEvent(AuthEvent.OnLogout)
                        },
                        onTotpSetupClick = { currentRoute = "totpSetup" },
                        showTotpSetup = authState.user?.totpEnabled != true,
                        appModule = appModule,
                        appScope = appScope,
                    )
                }
            }
            currentRoute == "notificationHistory" -> {
                NotificationHistoryScreen(
                    onBackClick = {
                        currentRoute = "postList"
                        window.history.pushState(null, "", "/")
                    },
                    appModule = appModule,
                    appScope = appScope,
                )
            }
            currentRoute == "notificationSettings" -> {
                NotificationSettingsScreen(
                    onBackClick = {
                        currentRoute = "postList"
                        window.history.pushState(null, "", "/")
                    },
                    appModule = appModule,
                    appScope = appScope,
                )
            }
            currentRoute == "stripeConnect" -> {
                // Redirect to login if not authenticated
                if (!authState.isLoggedIn) {
                    LaunchedEffect(Unit) {
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    }
                } else {
                    StripeConnectScreen(
                        onBackClick = {
                            currentRoute = "postList"
                            window.history.pushState(null, "", "/")
                        },
                        appModule = appModule,
                        appScope = appScope,
                    )
                }
            }
            currentRoute == "salesDashboard" -> {
                // Redirect to login if not authenticated
                if (!authState.isLoggedIn) {
                    LaunchedEffect(Unit) {
                        currentRoute = "emailLogin"
                        window.history.pushState(null, "", "/admin")
                    }
                } else {
                    SalesDashboardScreen(
                        onBackClick = {
                            currentRoute = "postList"
                            window.history.pushState(null, "", "/")
                        },
                        appModule = appModule,
                        appScope = appScope,
                    )
                }
            }
            else -> {
                // Error page for unknown routes
                Div({
                    style {
                        padding(40.px, 20.px)
                        textAlign("center")
                    }
                    attr("data-testid", "error-page")
                }) {
                    H1({
                        style {
                            fontSize(32.px)
                            marginBottom(16.px)
                            color(Color("#ff6b6b"))
                        }
                    }) {
                        Text("404 - ページが見つかりません")
                    }
                    P({
                        style {
                            fontSize(16.px)
                            color(Color("#666"))
                            marginBottom(24.px)
                        }
                    }) {
                        Text("お探しのページは存在しないか、移動した可能性があります。")
                    }
                    Button({
                        classes(AppStyleSheet.primaryButton)
                        onClick {
                            currentRoute = "postList"
                            window.history.pushState(null, "", "/")
                        }
                    }) {
                        Text("ホームに戻る")
                    }
                }
            }
        }
    }
}

